import React, { ReactElement, useRef } from "react";
import { Loading } from "../loading/Loading";
import { SummaryData } from "../../types/SummaryData";
import { useQuery } from "react-query";
import { showRequestErrorToast } from "../../services/ErrorService";
import DataRequest from "../../queries/DataRequest";
import { Summary as ReportSummary } from "@idwal/idwal-react-components";
import { useAuthenticator } from "@aws-amplify/ui-react";
import { AppContext } from "../../App";
import { AppContextType } from "../../types/AppContextType";
import { useLocation, useParams } from "react-router-dom";
import { getPhotos } from "../../queries/photosQueries";
import { LoadingShipImage } from "../loading/LoadingShipImage";
import { getVesselInspectionData } from "../../queries/particularsQueries";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { ProgressBar } from "primereact/progressbar";

export const Summary = ({ documentData }: { documentData: any }): ReactElement => {
    const { inspectionId } = useParams();
    const { t } = useTranslation("locale");
    const { user } = useAuthenticator((context) => [context.authStatus, context.user]);
    const { earlyRelease, restrictedView } = React.useContext(AppContext) as AppContextType;

    const locationSearch = useLocation();
    const queryParams = new URLSearchParams(locationSearch.search);
    const section = queryParams.get("section");
    const { data: photoData, isLoading, isError, error: photoError } = getPhotos(inspectionId ?? "", user);
    const { data: vesselData } = getVesselInspectionData(inspectionId ?? "", user);

    const toastId = useRef<null | string | number>(null);

    if (!inspectionId) {
        showRequestErrorToast("summary (id)");
    }

    const { error, data } = useQuery<SummaryData>(
        ["summary"],
        () => DataRequest(inspectionId ?? "", "summary", user)
    );

    if (error) {
        showRequestErrorToast("Summary");
        return <div></div>;
    }

    if (isLoading || !photoData) {
        return <LoadingShipImage />;
    }

    if (isError) {
        console.error(photoError);
        showRequestErrorToast("Vessel photos");
        return <div></div>;
    }

    const downloadCallback = async () => {
        const zip = new JSZip();
        toastId.current = toast(t("documents.preparingFilesForDownload"), {
            type: "success",
            closeButton: false,
            closeOnClick: false,
            progressClassName: "toast-progress-bar",
            icon: (
                <i
                    className="pi pi-check"
                    style={{
                        color: "var(--toastify-icon-color-success)",
                        fontSize: "1em",
                    }}
                ></i>
            ),
        });

        const totalFilesToDownload = photoData.flatMap((x) => x.images).length;
        let filesDownloaded = 0;

        try {
            for (const elementPhotos of photoData) {
                let folder = zip.folder(elementPhotos.albumName);

                if (!elementPhotos.images) {
                    continue;
                }

                const imageFetcher = await Promise.all(
                    elementPhotos.images.map(async (image) => {
                        const res = await fetch(image);
                        const blob = res.blob();

                        const progress = ++filesDownloaded / totalFilesToDownload;
                        const progressPercent = Math.round(progress * 100);
                        if (toastId.current) {
                            toast.update(toastId.current, {
                                progress: progress,
                                render: () => (
                                    <div>
                                        <p>{t("documents.preparingFilesForDownload")}</p>
                                        <ProgressBar
                                            className="file-progress"
                                            value={progressPercent}
                                            displayValueTemplate={() => (
                                                <span>{`${filesDownloaded}/${totalFilesToDownload}`}</span>
                                            )}
                                        ></ProgressBar>
                                    </div>
                                ),
                            });
                        }

                        return blob;
                    })
                );

                imageFetcher.forEach((blob, index) => {
                    const fileName = elementPhotos.albumName + "_" + index + ".jpg";
                    folder!.file(fileName, blob);
                });
            }

            zip.generateAsync({ type: "blob" }).then((content) => {
                saveAs(content, `${vesselData?.ShipName}_Photos.zip`);
            });
        } catch (error) {
            if (toastId.current) {
                toast.dismiss(toastId.current);
            }
            showRequestErrorToast(t("downloads.photosZipFailed"));
        }
    };

    if (data && documentData) {
        if (earlyRelease) {
            data.paragraph = "";
        }

        return (
            <ReportSummary
                paragraph={data.paragraph}
                photoData={photoData}
                documentData={documentData}
                section={section}
                restrictedView={restrictedView}
                earlyRelease={vesselData?.earlyRelease}
                downloadCallback={downloadCallback}
            />
        );
    }

    return <Loading />;
};